import { HgSupportedLanguage } from '@smg-real-estate/domus';
import type { GeoLocation, GeoLocationType } from '@smg-real-estate/geo-libs';

import { countryTextMapping } from '@/helpers/SEO/metaInfoHelper';
import { getAgentBasePathWithLocation, getBasePathWithLocation } from '@/router';
import type { DirectoryType } from '@/types/directoryType';

export interface DirectoryBreadcrumbLink {
  name: string;
  link?: string;
}

export interface BuildDirectoryBreadcrumbsParams {
  directoryType: DirectoryType;
  searchedLocations: GeoLocation[];
  language: HgSupportedLanguage;
  homepageLabel: string;
  directoryHubLabel: string;
  baseUrl?: string;
  getLocationsById: (ids: string[]) => GeoLocation[];
  getLocationName: (id: string, language: HgSupportedLanguage) => string | undefined;
}

const POSTCODE_LOCATION_TYPES = new Set<GeoLocationType>(['zip', 'ziparea']);

function resolveDirectoryBasePathWithLocation(
  directoryType: DirectoryType,
  language: HgSupportedLanguage,
  location?: string,
): string {
  return directoryType === 'agency'
    ? getBasePathWithLocation(language, location)
    : getAgentBasePathWithLocation(language, location);
}
const CITY_PARENT_TYPES = new Set<GeoLocationType>(['place', 'municipality']);

const HIERARCHY_ORDER: GeoLocationType[] = [
  'country',
  'canton',
  'district',
  'region',
  'municipality',
  'place',
  'cityregion',
  'citydistrict',
  'zip',
  'ziparea',
];

const isPostcodeLocation = (location: GeoLocation): boolean =>
  POSTCODE_LOCATION_TYPES.has(location.type);

const sortLocationsByHierarchy = (locations: GeoLocation[]): GeoLocation[] =>
  [...locations].sort((a, b) => HIERARCHY_ORDER.indexOf(a.type) - HIERARCHY_ORDER.indexOf(b.type));

const getParentLocations = (
  location: GeoLocation,
  getLocationsById: (ids: string[]) => GeoLocation[],
): GeoLocation[] => {
  if (location.type === 'canton') {
    return [];
  }

  if (!location.parentPaths || location.parentPaths.length === 0) {
    return [];
  }

  const parentPath = location.parentPaths[0];
  const parentIds = parentPath.filter(
    (id: string) => !id.startsWith('geo-zipcode') && !id.startsWith('geo-country'),
  );
  const resolvedParents = getLocationsById(parentIds);

  const allowedTypes = isPostcodeLocation(location)
    ? new Set<GeoLocationType>(['canton', ...CITY_PARENT_TYPES])
    : new Set<GeoLocationType>(['canton']);

  return sortLocationsByHierarchy(
    resolvedParents.filter((parent) => allowedTypes.has(parent.type)),
  );
};

const generateLocationLinks = ({
  locations,
  language,
  baseUrl,
  directoryType,
  getLocationName,
}: {
  locations: GeoLocation[];
  language: HgSupportedLanguage;
  baseUrl: string;
  directoryType: DirectoryType;
  getLocationName: (id: string, language: HgSupportedLanguage) => string | undefined;
}): DirectoryBreadcrumbLink[] => {
  const sortedLocations = sortLocationsByHierarchy(locations);

  return sortedLocations.map((location, index) => {
    const locationName = getLocationName(location.newId ?? location.id, language) ?? '';
    const urlName = location.urlNames?.[language] || location.urlNames?.[HgSupportedLanguage.DE];
    const isLast = index === sortedLocations.length - 1;

    return {
      name: locationName,
      link: isLast
        ? undefined
        : `${baseUrl}${resolveDirectoryBasePathWithLocation(directoryType, language, urlName)}`,
    };
  });
};

/**
 * Build the visible breadcrumb trail for Agency/Agent directory pages.
 * Shared by Domus breadcrumbs and JSON-LD BreadcrumbList so both stay in sync.
 */
export function buildDirectoryBreadcrumbs({
  directoryType,
  searchedLocations,
  language,
  homepageLabel,
  directoryHubLabel,
  baseUrl = import.meta.env.VUE_APP_BASE_URL,
  getLocationsById,
  getLocationName,
}: BuildDirectoryBreadcrumbsParams): DirectoryBreadcrumbLink[] {
  const homepageLink: DirectoryBreadcrumbLink = {
    name: homepageLabel,
    link: baseUrl,
  };

  if (searchedLocations.length === 0) {
    return [
      homepageLink,
      {
        name: countryTextMapping[language],
      },
    ];
  }

  const directoryHubLink: DirectoryBreadcrumbLink = {
    name: directoryHubLabel,
    link: `${baseUrl}${resolveDirectoryBasePathWithLocation(directoryType, language)}`,
  };

  const primaryLocation = searchedLocations[0];
  const parentLocations = getParentLocations(primaryLocation, getLocationsById);
  const allLocations = [...parentLocations, primaryLocation];
  const locationLinks = generateLocationLinks({
    locations: allLocations,
    language,
    baseUrl,
    directoryType,
    getLocationName,
  });

  if (isPostcodeLocation(primaryLocation)) {
    return [homepageLink, ...locationLinks];
  }

  return [homepageLink, directoryHubLink, ...locationLinks];
}
